import { useEffect, useRef, useState } from "react";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  CheckIcon,
} from "./AuthIcons";
import OtpInput from "./OtpInput";
import {
  SESSION_ENDING_CODES,
  resendOtpRequest,
  sessionEndedMessage,
  verifyOtpRequest,
} from "./authApi";
import useCountdown, { formatClock } from "./useCountdown";

const CODE_LENGTH = 6;
const SUCCESS_HOLD_MS = 450; // long enough to register the check mark, short enough to feel instant
const NOTICE_MS = 5000;

const attemptsText = (left) =>
  left > 0
    ? `That code isn't right. ${left} ${left === 1 ? "attempt" : "attempts"} left.`
    : "That code isn't right.";

// The single line under the boxes: the most important thing first. Only things worth announcing
// live here; the ticking countdown of the last minute is rendered separately (see `closing`).
const describeStatus = ({ status, message, notice, expired }) => {
  if (status === "success") {
    return { tone: "success", line: "Verified. Signing you in…", Icon: CheckCircleIcon };
  }
  if (message) return { tone: "error", line: message, Icon: AlertCircleIcon };
  if (notice) return { tone: "success", line: notice, Icon: CheckCircleIcon };
  if (expired) {
    return {
      tone: "error",
      line: "This code has expired. Send a new one below.",
      Icon: AlertCircleIcon,
    };
  }
  return { tone: "neutral", line: "", Icon: null };
};

/**
 * Clock side of a pending code: when it expires, when another one may be requested and how
 * many requests remain. Everything is derived from absolute deadlines, so it stays right
 * however the tab was throttled.
 */
function useCodeSchedule(challenge) {
  const [schedule, setSchedule] = useState(() => ({
    lifetime: challenge.expiresIn,
    expiresAt: challenge.receivedAt + challenge.expiresIn * 1000,
    resendAt: challenge.receivedAt + challenge.resendAfter * 1000,
    resendsLeft: challenge.resendsLeft,
  }));
  const expirySeconds = useCountdown(schedule.expiresAt);
  const resendSeconds = useCountdown(schedule.resendAt);

  return {
    /** How long a code lives, in seconds, as the server last reported it. */
    lifetime: schedule.lifetime,
    expirySeconds,
    resendSeconds,
    expired: expirySeconds === 0,
    canResend: schedule.resendsLeft > 0,
    /** A new code went out: `{ expiresIn, resendAfter, resendsLeft }` as the server reports it. */
    renew: ({ expiresIn, resendAfter, resendsLeft }) => {
      const now = Date.now();
      setSchedule({
        lifetime: expiresIn,
        expiresAt: now + expiresIn * 1000,
        resendAt: now + resendAfter * 1000,
        resendsLeft,
      });
    },
    /** The server rejected the code as expired. */
    expireNow: () => setSchedule((current) => ({ ...current, expiresAt: Date.now() })),
    /** The server asked us to wait `seconds` before requesting another code. */
    waitBeforeResend: (seconds) =>
      setSchedule((current) => ({
        ...current,
        resendAt: Date.now() + Math.max(1, seconds) * 1000,
      })),
  };
}

/** The masked address, allowed to wrap after the "@" so a narrow screen never splits the domain. */
function MaskedEmail({ value }) {
  const at = value.indexOf("@");
  if (at < 0) return value;
  return (
    <>
      {value.slice(0, at + 1)}
      <wbr />
      {value.slice(at + 1)}
    </>
  );
}

/** Content of the main button for each phase of the check. */
function VerifyLabel({ status }) {
  if (status === "verifying") {
    return (
      <>
        <span className="mrc-auth__spinner" aria-hidden="true" />
        Verifying…
      </>
    );
  }
  if (status === "success") {
    return (
      <>
        <CheckIcon size={18} />
        Signed in
      </>
    );
  }
  return "Verify and sign in";
}

/** "Didn't get the code?" followed by a countdown, the resend link, or "none left". */
function ResendRow({ muted, busy, resending, secondsLeft, canResend, onResend }) {
  let action;
  if (secondsLeft > 0) {
    action = (
      <span className="mrc-auth__resend-wait">
        Resend in <span className="mrc-auth__tabular">{formatClock(secondsLeft)}</span>
      </span>
    );
  } else if (!canResend) {
    action = <span className="mrc-auth__resend-wait">No more codes for this sign-in.</span>;
  } else {
    action = (
      <button type="button" className="mrc-auth__link" onClick={onResend} disabled={busy}>
        {resending ? "Sending…" : "Resend code"}
      </button>
    );
  }

  return (
    <div className="mrc-auth__resend" data-muted={muted || undefined}>
      <span>Didn't get the code?</span>
      {action}
    </div>
  );
}

/**
 * Second step of sign-in: enter the code that was e-mailed.
 *
 * props
 *   challenge   { id, email (masked), expiresIn, resendAfter, resendsLeft, receivedAt }
 *   enter       "forward" | "back" | "none"  (which way the step slides in)
 *   leaving     null, or "forward" | "back" while the step is animating out
 *   onVerified  (response) => void   called once the server accepted the code
 *   onBack      (notice?) => void    back to the credentials form (optionally with a message)
 */
export default function OtpStep({ challenge, enter, leaving, onVerified, onBack }) {
  const {
    lifetime,
    expirySeconds,
    resendSeconds,
    expired,
    canResend,
    renew,
    expireNow,
    waitBeforeResend,
  } = useCodeSchedule(challenge);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle"); // idle | verifying | error | success
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [shake, setShake] = useState(0);
  const [resending, setResending] = useState(false);

  const otpRef = useRef(null);
  const submitting = useRef(false);
  const finishTimer = useRef(null);

  const busy = status === "verifying" || status === "success" || resending;

  // Keep the caret where typing continues: on entry, and after every failed / finished attempt.
  useEffect(() => {
    if (status === "idle" || status === "error") otpRef.current?.focus();
  }, [status]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => () => clearTimeout(finishTimer.current), []);

  const handleCodeChange = (next) => {
    setCode(next);
    if (status === "error") setStatus("idle");
    if (message) setMessage("");
    if (notice) setNotice("");
  };

  const handleVerifyFailure = (error) => {
    if (SESSION_ENDING_CODES.has(error.code)) {
      onBack(sessionEndedMessage(error));
      return;
    }

    switch (error.code) {
      case "OTP_INVALID":
        setCode("");
        setStatus("error");
        setShake((count) => count + 1);
        setMessage(attemptsText(error.attemptsLeft));
        break;
      case "OTP_EXPIRED":
        expireNow(); // the server's word is final: line the local clock up with it
        setCode("");
        setStatus("error");
        break;
      case "OTP_INVALID_FORMAT":
        setStatus("error");
        setMessage("Enter the 6-digit code from your email.");
        break;
      default:
        // network / server trouble: keep the digits so retrying is a single click
        setStatus("idle");
        setMessage(error.message || "We couldn't verify the code. Please try again.");
    }
  };

  const submit = async (value) => {
    if (submitting.current) return;
    if (value.length !== CODE_LENGTH) {
      setStatus("error");
      setMessage("Enter the 6-digit code from your email.");
      return;
    }

    submitting.current = true;
    setStatus("verifying");
    setMessage("");
    setNotice("");

    try {
      const response = await verifyOtpRequest(challenge.id, value);
      if (response?.success !== "true" || !response.token) {
        throw new Error("Unexpected response");
      }
      setStatus("success"); // stays locked: the page is about to move on
      finishTimer.current = setTimeout(() => onVerified(response), SUCCESS_HOLD_MS);
    } catch (error) {
      submitting.current = false;
      handleVerifyFailure(error);
    }
  };

  const handleResend = async () => {
    if (busy || resendSeconds > 0 || !canResend) return;

    setResending(true);
    setMessage("");
    setNotice("");
    try {
      renew(await resendOtpRequest(challenge.id));
      setCode("");
      setStatus("idle");
      setNotice("New code sent. Check your inbox.");
      otpRef.current?.focus(0);
    } catch (error) {
      if (SESSION_ENDING_CODES.has(error.code)) {
        onBack(sessionEndedMessage(error));
        return;
      }
      if (error.code === "OTP_RESEND_COOLDOWN") {
        waitBeforeResend(error.retryAfter);
        return;
      }
      setMessage(error.message || "We couldn't send a new code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!busy && !expired) submit(code);
  };

  const { tone, line, Icon: LineIcon } = describeStatus({ status, message, notice, expired });

  // Last minute with nothing else to say: a quiet countdown takes the empty status line. It sits
  // outside the live region on purpose (a value that changes every second must not be announced
  // every second) and is one stable element, so only its digits change instead of re-fading.
  const closing = !line && !expired && expirySeconds <= 60;

  const minutes = Math.max(1, Math.round(lifetime / 60));
  const boxStatus =
    status === "success" ? "success" : expired ? "expired" : status === "error" ? "error" : "idle";

  return (
    <form
      className="mrc-auth__step"
      data-enter={enter}
      data-leaving={leaving || undefined}
      onSubmit={handleSubmit}
      noValidate
      aria-busy={busy || undefined}
    >
      <h1 id="mrc-auth-otp-title" className="mrc-auth__title">
        Check your email
      </h1>
      <p id="mrc-auth-otp-lead" className="mrc-auth__lead">
        We sent a 6-digit code to{" "}
        <strong className="mrc-auth__lead-target">
          <MaskedEmail value={challenge.email} />
        </strong>{" "}
        Codes expire after {minutes} {minutes === 1 ? "minute" : "minutes"}.
      </p>

      <OtpInput
        ref={otpRef}
        length={CODE_LENGTH}
        value={code}
        onChange={handleCodeChange}
        onComplete={submit}
        status={boxStatus}
        disabled={status === "verifying" || status === "success" || expired}
        shake={shake}
        labelledBy="mrc-auth-otp-title"
        describedBy="mrc-auth-otp-lead mrc-auth-otp-status"
      />

      <div className="mrc-auth__status" data-tone={tone}>
        <div id="mrc-auth-otp-status" aria-live="polite" aria-atomic="true">
          {line && (
            <span key={line} className="mrc-auth__status-line">
              {LineIcon && <LineIcon size={16} />}
              <span>{line}</span>
            </span>
          )}
        </div>
        {closing && (
          <span className="mrc-auth__status-line" role="timer">
            <span>
              Code expires in <span className="mrc-auth__tabular">{formatClock(expirySeconds)}</span>
            </span>
          </span>
        )}
      </div>

      <button
        type="submit"
        className="mrc-auth__button"
        disabled={code.length !== CODE_LENGTH || busy || expired}
        data-loading={status === "verifying" || undefined}
        data-success={status === "success" || undefined}
      >
        <VerifyLabel status={status} />
      </button>

      <ResendRow
        muted={status === "success"}
        busy={busy}
        resending={resending}
        secondsLeft={resendSeconds}
        canResend={canResend}
        onResend={handleResend}
      />

      <button
        type="button"
        className="mrc-auth__back"
        onClick={() => onBack()}
        disabled={status === "verifying" || status === "success"}
      >
        <ArrowLeftIcon size={16} />
        Use a different account
      </button>
    </form>
  );
}
